import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Video } from "lucide-react";
import { Header, scrollToId } from "@/components/Header";
import { EnquiryForm } from "@/components/EnquiryForm";
import { MobileActionBar } from "@/components/MobileActionBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, SITE_URL } from "@/hooks/useSeo";
import { LOCATIONS, CHARTER_CHIPS } from "@/data/locations";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.14 },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function LocationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = LOCATIONS.find((l) => l.slug === slug);
  const imgWrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgWrapRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  useEffect(() => {
    if (!location) navigate("/", { replace: true });
  }, [location, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useSeo(
    location
      ? {
          title: location.seoTitle,
          description: location.seoDescription,
          path: `/locations/${location.slug}`,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "FinancialService",
              name: `NUA Private Wealth — Independent Financial Adviser ${location.name}`,
              description: location.seoDescription,
              url: `${SITE_URL}/locations/${location.slug}`,
              areaServed: [location.name, location.region],
              address: {
                "@type": "PostalAddress",
                streetAddress: "Office 845, Level 8, 121 Marcus Clarke Street",
                addressLocality: "Canberra",
                addressRegion: "ACT",
                postalCode: "2601",
                addressCountry: "AU",
              },
              telephone: "+61 411 842 648",
              email: "hello@nuaprivatewealth.com.au",
              priceRange: "Fixed-fee, no commissions",
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: `Independent Financial Adviser ${location.name}`,
                  item: `${SITE_URL}/locations/${location.slug}`,
                },
              ],
            },
          ],
        }
      : {}
  );

  if (!location) return null;

  const lines = ["Independent Financial", `Adviser ${location.name}.`];

  return (
    <>
      <Header />
      <main>
        <section data-testid="location-hero" className="relative overflow-hidden bg-[#F9F8F4] pt-16 md:pt-[88px]">
          <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
            <div className="md:col-span-7 pt-10 md:pt-20">
              <motion.img
                src="/assets/nua-mark-transparent.png"
                alt=""
                aria-hidden="true"
                data-testid="location-triskele"
                {...fade(0)}
                className="mb-8 h-10 md:h-12 w-auto opacity-90"
              />
              <motion.p
                data-testid="location-overline"
                {...fade(0.1)}
                className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]"
              >
                <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                Independent Financial Advice — {location.name}
              </motion.p>

              <h1
                data-testid="location-heading"
                className="mt-8 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05] text-[#111413]"
              >
                {lines.map((line, i) => (
                  <span key={line} className="block overflow-hidden pb-1">
                    <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="show">
                      {i === 1 ? (
                        <>
                          Adviser <em className="italic text-[#0A2333]">{location.name}.</em>
                        </>
                      ) : (
                        line
                      )}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                data-testid="location-subcopy"
                {...fade(0.75)}
                className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]"
              >
                {location.subcopy}
              </motion.p>

              <motion.div {...fade(0.9)} className="mt-10 flex flex-wrap items-center gap-5">
                <button
                  data-testid="location-cta-book"
                  onClick={() => scrollToId("contact")}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-7 py-3.5 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5"
                >
                  Book a consultation
                  <ArrowUpRight size={15} />
                </button>
                <button
                  data-testid="location-cta-charter"
                  onClick={() => scrollToId("charter")}
                  className="text-xs uppercase tracking-[0.15em] text-[#111413] underline decoration-[#A78558] decoration-2 underline-offset-8 hover:text-[#0A2333] transition-colors duration-300"
                >
                  Read the Independence Charter
                </button>
              </motion.div>

              <motion.div {...fade(1.05)} className="mt-12 pb-16 flex flex-wrap gap-3" data-testid="location-chips">
                {CHARTER_CHIPS.map((chip) => (
                  <span
                    key={chip}
                    className="border border-[#0A2333]/30 text-[#0A2333] text-[10px] uppercase tracking-[0.2em] px-4 py-2"
                  >
                    {chip}
                  </span>
                ))}
              </motion.div>
            </div>

            <div className="md:col-span-5 md:pt-12 pb-16 md:pb-24">
              <motion.div
                ref={imgWrapRef}
                initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
                animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
                transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="relative overflow-hidden rounded-t-[10rem] h-[52vh] md:h-[70vh] border border-black/10"
                data-testid="location-image-frame"
              >
                <motion.img
                  src={location.image}
                  alt={location.imageAlt}
                  style={{ y }}
                  className="absolute inset-0 h-[120%] w-full object-cover"
                  data-testid="location-image"
                />
              </motion.div>
              <motion.p {...fade(1.2)} className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#4A524D]">
                {location.imageCaption}
              </motion.p>
            </div>
          </div>
        </section>

        <section data-testid="location-profiles" className="bg-[#EFEBE2] py-16 md:py-36">
          <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="md:sticky md:top-28">
                <Reveal>
                  <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
                    <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                    Local focus
                  </p>
                  <h2 data-testid="location-profiles-heading" className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#111413]">
                    Who we work with in {location.name}.
                  </h2>
                  <p className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-[#4A524D]" data-testid="location-remote-note">
                    <Video size={16} className="mt-0.5 shrink-0 text-[#A78558]" />
                    {location.note}
                  </p>
                </Reveal>
              </div>
            </div>

            <div className="md:col-span-8">
              {location.profiles.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.06}>
                  <div
                    data-testid={`location-profile-${i + 1}`}
                    className="grid grid-cols-12 items-baseline gap-x-4 gap-y-2 border-t border-black/15 px-2 md:px-4 py-8 transition-[background-color] duration-300 hover:bg-[#F9F8F4]"
                  >
                    <span className="col-span-2 md:col-span-1 font-serif italic text-lg text-[#A78558]">
                      0{i + 1}
                    </span>
                    <h3 className="col-span-10 md:col-span-4 font-serif text-2xl md:text-3xl tracking-tight text-[#111413]">
                      {p.title}
                    </h3>
                    <p className="col-span-10 col-start-3 md:col-span-7 md:col-start-6 text-sm leading-relaxed text-[#4A524D]">
                      {p.body}
                    </p>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-black/15" aria-hidden="true" />
            </div>
          </div>
        </section>

        {location.towns && (
          <section data-testid="location-communities" className="bg-[#F9F8F4] py-16 md:py-36">
            <div className="mx-auto max-w-7xl px-6 md:px-10">
              <Reveal>
                <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
                  <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                  Serving Local Communities
                </p>
                <h2 data-testid="location-communities-heading" className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#111413] max-w-2xl">
                  From Mollymook to Eden — independent advice, close to home.
                </h2>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4A524D]">
                  Wherever you are on the South Coast, you get the same fixed-fee, no-product advice —
                  by secure video, or in person when we're in your town. Here's who we help in each community.
                </p>
              </Reveal>

              <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {location.towns.map((t, i) => (
                  <Reveal key={t.name} delay={i * 0.06}>
                    <article
                      data-testid={`community-card-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group flex h-full flex-col overflow-hidden border border-black/10 bg-white transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={t.image}
                          alt={t.imageAlt}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2333]/50 to-transparent" aria-hidden="true" />
                        <h3 className="absolute bottom-4 left-5 font-serif text-2xl tracking-tight text-[#F9F8F4]">
                          {t.name}
                        </h3>
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <p className="text-sm leading-relaxed text-[#4A524D]">{t.blurb}</p>
                        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 pt-4 border-t border-black/10">
                          <button
                            data-testid={`community-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => scrollToId("contact")}
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-[#0A2333] hover:text-[#071824] transition-colors duration-300"
                          >
                            Book a consultation
                            <ArrowUpRight size={14} />
                          </button>
                          <a
                            href={t.mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] uppercase tracking-[0.15em] text-[#A78558] hover:text-[#8A6A43] transition-colors duration-300"
                          >
                            View area
                          </a>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={0.1}>
                <div className="mt-14 flex flex-wrap items-center gap-5 border-t border-black/10 pt-10">
                  <p className="font-serif text-xl md:text-2xl tracking-tight text-[#111413] max-w-lg">
                    Ready to talk about your plans on the coast?
                  </p>
                  <button
                    data-testid="location-communities-cta"
                    onClick={() => scrollToId("contact")}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-7 py-3.5 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5"
                  >
                    Book a consultation
                    <ArrowUpRight size={15} />
                  </button>
                </div>
              </Reveal>
            </div>
          </section>
        )}

        <EnquiryForm />

        <section data-testid="location-closing" className="bg-[#0A2333] border-t border-white/10 py-16 md:py-20">
          <Reveal>
            <p className="text-center font-serif italic text-2xl md:text-4xl text-[#A78558] px-6">
              Wealth creates freedom for what's next.
            </p>
          </Reveal>
        </section>
      </main>
      <Footer />
      <MobileActionBar />
    </>
  );
}
